import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Report } from './Report';
import { getCurrencySymbol } from '../../../util/productUtils';
import { captureReportCharts, CapturedChart } from './reportChartRenderer';

export class ReportPDFGenerator {
  private static readonly COLORS = {
    primary:   '#1976d2',
    secondary: '#424242',
    text:      '#212121',
    lightGray: '#f5f5f5',
    border:    '#e0e0e0',
  };

  // ── formatting helpers ─────────────────────────────────────────────────────

  private static fmtAmt(n: number, sym: string): string {
    return `${sym} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  private static fmtNum(n: number): string {
    return n.toLocaleString('en-US');
  }

  // ── chart embedding helper ───────────────────────────────────────────────────
  // Looks up a captured chart by title (see reportChartRenderer.tsx — titles
  // match exactly what ReportDetailsDialog.tsx shows on screen), draws a
  // heading, and places the PNG below it, paginating first if it wouldn't fit.
  private static addChartImage(
    doc: jsPDF, title: string, charts: CapturedChart[], y: number, pw: number, ph: number
  ): number {
    const chart = charts.find(c => c.title === title);
    if (!chart) return y;

    const imgWidth = pw - 30;
    const imgHeight = imgWidth * (chart.pixelHeight / chart.pixelWidth);
    const blockHeight = imgHeight + 12;

    if (y + blockHeight > ph - 15) {
      doc.addPage();
      y = 20;
    }

    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(this.COLORS.text);
    doc.text(title, 15, y);
    y += 5;
    doc.addImage(chart.dataUrl, 'PNG', 15, y, imgWidth, imgHeight);
    return y + imgHeight + 10;
  }

  // ── main entry ─────────────────────────────────────────────────────────────

  static async generatePDF(report: Report, currency = 'LKR'): Promise<void> {
    const sym  = getCurrencySymbol(currency);
    const doc  = new jsPDF();
    const pw   = doc.internal.pageSize.getWidth();
    const ph   = doc.internal.pageSize.getHeight();
    let y      = 20;

    // Off-screen render the same charts shown in ReportDetailsDialog.tsx and
    // capture them as PNGs, so the PDF isn't text/tables-only anymore.
    const chartImages = await captureReportCharts(report, sym);

    // Header band
    doc.setFillColor(this.COLORS.primary);
    doc.rect(0, 0, pw, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20); doc.setFont('helvetica', 'bold');
    doc.text(report.reportName, 15, 20);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${report.generatedAt?.toLocaleString()}`, 15, 30);
    doc.text(`By: ${report.generatedByName}`, 15, 35);
    y = 50;

    doc.setTextColor(this.COLORS.text);
    doc.setFontSize(10);

    if (report.description) {
      doc.setFont('helvetica', 'bold'); doc.text('Description:', 15, y);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(report.description, pw - 30);
      doc.text(lines, 15, y + 5);
      y += lines.length * 5 + 10;
    }

    if (report.filters && Object.keys(report.filters).length > 0) {
      doc.setFont('helvetica', 'bold'); doc.text('Filters Applied:', 15, y); y += 7;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
      if (report.filters.dateFrom) { doc.text(`Date From: ${report.filters.dateFrom.toLocaleDateString()}`, 20, y); y += 5; }
      if (report.filters.dateTo)   { doc.text(`Date To: ${report.filters.dateTo.toLocaleDateString()}`,     20, y); y += 5; }
      y += 5; doc.setFontSize(10);
    }

    switch (report.reportType) {
      case 'sales':                  this.generateSalesReport(doc, report, y, sym, chartImages);                break;
      case 'inventory':              this.generateInventoryReport(doc, report, y, sym);            break;
      case 'purchase_orders':        this.generatePurchaseOrderReport(doc, report, y, sym, chartImages);        break;
      case 'low_stock':              this.generateLowStockReport(doc, report, y, sym);             break;
      case 'warehouse_performance':  this.generateWarehousePerformanceReport(doc, report, y, sym); break;
      case 'product_performance':    this.generateProductPerformanceReport(doc, report, y, sym, chartImages);   break;
      case 'revenue':                this.generateRevenueReport(doc, report, y, sym, chartImages);              break;
      case 'returns_cancellations':  this.generateReturnsCancellationsReport(doc, report, y, sym, chartImages); break;
      case 'profit_loss':            this.generateProfitLossReport(doc, report, y, sym, chartImages);           break;
      case 'cost':                   this.generateCostReport(doc, report, y, sym, chartImages);                break;
      case 'payments':               this.generatePaymentsReport(doc, report, y, sym, chartImages);            break;
      case 'forecast':               this.generateForecastReport(doc, report, y, sym, chartImages);            break;
    }

    // Page footers
    const total = doc.internal.pages.length - 1;
    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      doc.setFontSize(8); doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${total}`, pw / 2, ph - 10, { align: 'center' });
    }

    doc.save(`${report.reportName.replace(/\s+/g, '_')}_${Date.now()}.pdf`);
  }

  // ── Sales ──────────────────────────────────────────────────────────────────

  private static generateSalesReport(doc: jsPDF, report: Report, y: number, sym: string, charts: CapturedChart[] = []): void {
    const data = report.data;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Summary', 15, y); y += 10;

    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Total Orders',        this.fmtNum(data.summary.totalOrders)],
        ['Total Revenue',       this.fmtAmt(data.summary.totalRevenue, sym)],
        ['Average Order Value', this.fmtAmt(data.summary.averageOrderValue, sym)],
        ['Total Tax',           this.fmtAmt(data.summary.totalTax, sym)],
        ['Total Shipping',      this.fmtAmt(data.summary.totalShipping, sym)],
        ['Total Discount',      this.fmtAmt(data.summary.totalDiscount, sym)],
      ],
      theme: 'plain',
      headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
      margin: { left: 15 }, tableWidth: 90,
    });
    y = (doc as any).lastAutoTable.finalY + 15;

    const pw = doc.internal.pageSize.getWidth(), ph = doc.internal.pageSize.getHeight();
    y = this.addChartImage(doc, 'Top Products by Revenue', charts, y, pw, ph);
    y = this.addChartImage(doc, 'Top Products Share', charts, y, pw, ph);

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Orders (Delivered)', 15, y); y += 7;
    autoTable(doc, {
      startY: y,
      head: [['Order #', 'Customer', 'Date', 'Amount', 'Payment', 'Status']],
      body: data.orders.map((o: any) => [
        o.orderNumber, o.customerName,
        o.orderDate instanceof Date ? o.orderDate.toLocaleDateString() : 'N/A',
        this.fmtAmt(o.totalAmount, sym), o.paymentMethod, o.status,
      ]),
      theme: 'striped',
      headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
      alternateRowStyles: { fillColor: this.COLORS.lightGray },
      margin: { left: 15, right: 15 }, styles: { fontSize: 8 },
    });
  }

  // ── Inventory ──────────────────────────────────────────────────────────────

  private static generateInventoryReport(doc: jsPDF, report: Report, y: number, sym: string): void {
    const data = report.data;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Summary', 15, y); y += 10;

    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Total Products',      this.fmtNum(data.summary.totalProducts)],
        ['Total Variants',      this.fmtNum(data.summary.totalVariants)],
        ['Total Stock',         this.fmtNum(data.summary.totalStock)],
        ['Stock Value',         this.fmtAmt(data.summary.totalValue, sym)],
        ['In Stock Items',      this.fmtNum(data.summary.inStockItems)],
        ['Low Stock Items',     this.fmtNum(data.summary.lowStockItems)],
        ['Out of Stock Items',  this.fmtNum(data.summary.outOfStockItems)],
      ],
      theme: 'plain',
      headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
      margin: { left: 15 }, tableWidth: 80,
    });
    y = (doc as any).lastAutoTable.finalY + 15;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Products', 15, y); y += 7;
    autoTable(doc, {
      startY: y,
      head: [['Product', 'SKU', 'Category', 'Stock', 'Value', 'Status']],
      body: data.products.map((p: any) => [
        p.productName + (p.hasVariants ? ` (${p.variants?.length || 0} variants)` : ''),
        p.sku, p.category, this.fmtNum(p.totalStock), this.fmtAmt(p.stockValue, sym), p.status.replace('_', ' '),
      ]),
      theme: 'striped',
      headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
      alternateRowStyles: { fillColor: this.COLORS.lightGray },
      margin: { left: 15, right: 15 }, styles: { fontSize: 8 },
    });
  }

  // ── Purchase Orders ────────────────────────────────────────────────────────

  private static generatePurchaseOrderReport(doc: jsPDF, report: Report, y: number, sym: string, charts: CapturedChart[] = []): void {
    const data = report.data;
    if (!data.purchaseOrders || data.purchaseOrders.length === 0) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(12);
      doc.text('No purchase orders found for the selected criteria.', 15, y);
      return;
    }

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Summary', 15, y); y += 10;
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Total Purchase Orders', this.fmtNum(data.summary.totalPOs)],
        ['Total Amount',          this.fmtAmt(data.summary.totalAmount || 0, sym)],
        ['Total Paid',            this.fmtAmt(data.summary.totalPaid   || 0, sym)],
        ['Total Pending',         this.fmtAmt((data.summary.totalAmount || 0) - (data.summary.totalPaid || 0), sym)],
      ],
      theme: 'plain',
      headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
      margin: { left: 15 }, tableWidth: 80,
    });
    y = (doc as any).lastAutoTable.finalY + 15;

    {
      const pw = doc.internal.pageSize.getWidth(), ph = doc.internal.pageSize.getHeight();
      y = this.addChartImage(doc, 'POs by Status', charts, y, pw, ph);
      y = this.addChartImage(doc, 'Top Suppliers by Spend', charts, y, pw, ph);
    }

    if (data.summary.statusBreakdown && Object.keys(data.summary.statusBreakdown).length > 0) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('Status Breakdown', 15, y); y += 7;
      autoTable(doc, {
        startY: y,
        head: [['Status', 'Count']],
        body: Object.entries(data.summary.statusBreakdown as Record<string, number>).map(([s, c]) => [s, c.toString()]),
        theme: 'plain',
        headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
        margin: { left: 15 }, tableWidth: 60,
      });
      y = (doc as any).lastAutoTable.finalY + 10;
    }

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Purchase Orders', 15, y); y += 7;
    autoTable(doc, {
      startY: y,
      head: [['PO #', 'Supplier', 'Order Date', 'Exp. Delivery', 'Amount', 'Status', 'Payment']],
      body: data.purchaseOrders.map((po: any) => [
        po.poNumber || 'N/A', po.supplierName || 'Unknown',
        po.orderDate instanceof Date ? po.orderDate.toLocaleDateString() : 'N/A',
        po.expectedDelivery instanceof Date ? po.expectedDelivery.toLocaleDateString() : 'N/A',
        this.fmtAmt(po.totalAmount || 0, sym), po.status || 'draft', po.paymentStatus || 'pending',
      ]),
      theme: 'striped',
      headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
      alternateRowStyles: { fillColor: this.COLORS.lightGray },
      margin: { left: 15, right: 15 }, styles: { fontSize: 8 },
      columnStyles: { 0: { cellWidth: 28 }, 1: { cellWidth: 38 }, 2: { cellWidth: 24 }, 3: { cellWidth: 24 }, 4: { cellWidth: 26 }, 5: { cellWidth: 22 }, 6: { cellWidth: 22 } },
    });
  }

  // ── Low Stock ──────────────────────────────────────────────────────────────

  private static generateLowStockReport(doc: jsPDF, report: Report, y: number, sym: string): void {
    const data = report.data;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Summary', 15, y); y += 10;
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Low Stock Items',      this.fmtNum(data.summary.totalLowStockItems)],
        ['Out of Stock Items',   this.fmtNum(data.summary.totalOutOfStockItems)],
        ['Affected Products',    this.fmtNum(data.summary.affectedProducts)],
        ['Affected Warehouses',  this.fmtNum(data.summary.affectedWarehouses)],
      ],
      theme: 'plain',
      headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
      margin: { left: 15 }, tableWidth: 80,
    });
    y = (doc as any).lastAutoTable.finalY + 15;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Low Stock Items', 15, y); y += 7;
    autoTable(doc, {
      startY: y,
      head: [['Product', 'Warehouse', 'Current', 'Min', 'Deficit', 'Status']],
      body: data.items.map((item: any) => [
        item.productName + (item.variantName ? ` (${item.variantName})` : ''),
        item.warehouseName, this.fmtNum(item.currentStock), this.fmtNum(item.minStock), this.fmtNum(item.deficit),
        item.status === 'out_of_stock' ? 'Out of Stock' : 'Low Stock',
      ]),
      theme: 'striped',
      headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
      alternateRowStyles: { fillColor: this.COLORS.lightGray },
      margin: { left: 15, right: 15 }, styles: { fontSize: 8 },
    });
  }

  // ── Warehouse Performance ──────────────────────────────────────────────────

  private static generateWarehousePerformanceReport(doc: jsPDF, report: Report, y: number, sym: string): void {
    const data = report.data;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Summary', 15, y); y += 10;
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Total Warehouses',   this.fmtNum(data.summary.totalWarehouses)],
        ['Total Capacity',     this.fmtNum(data.summary.totalCapacity)],
        ['Total Used',         this.fmtNum(data.summary.totalUsed)],
        ['Total Available',    this.fmtNum(data.summary.totalAvailable)],
        ['Avg Utilization',    `${data.summary.averageUtilization}%`],
        ['Most Utilized',      data.summary.mostUtilizedWarehouse],
        ['Least Utilized',     data.summary.leastUtilizedWarehouse],
      ],
      theme: 'plain',
      headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
      margin: { left: 15 }, tableWidth: 100,
    });
    y = (doc as any).lastAutoTable.finalY + 15;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Warehouses', 15, y); y += 7;
    autoTable(doc, {
      startY: y,
      head: [['Warehouse', 'Code', 'Products', 'Stock', 'Capacity', 'Utilization']],
      body: data.warehouses.map((w: any) => [
        w.warehouseName, w.code, this.fmtNum(w.totalProducts), this.fmtNum(w.totalStock),
        `${this.fmtNum(w.capacityUsed)}/${this.fmtNum(w.capacityTotal)}`, `${w.utilizationRate}%`,
      ]),
      theme: 'striped',
      headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
      alternateRowStyles: { fillColor: this.COLORS.lightGray },
      margin: { left: 15, right: 15 }, styles: { fontSize: 8 },
    });
  }

  // ── Product Performance ────────────────────────────────────────────────────

  private static generateProductPerformanceReport(doc: jsPDF, report: Report, y: number, sym: string, charts: CapturedChart[] = []): void {
    const data = report.data;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Summary', 15, y); y += 10;
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Total Products',      this.fmtNum(data.summary.totalProducts)],
        ['Total Revenue',       this.fmtAmt(data.summary.totalRevenue, sym)],
        ['Total Units Sold',    this.fmtNum(data.summary.totalUnitsSold)],
        ['Best Selling Product',data.summary.bestSellingProduct],
        ['Avg Stock Turnover',  data.summary.averageStockTurnover.toString()],
        ['Avg Discount',        `${data.summary.averageDiscount.toFixed(2)}%`],
      ],
      theme: 'plain',
      headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
      margin: { left: 15 }, tableWidth: 100,
    });
    y = (doc as any).lastAutoTable.finalY + 15;

    y = this.addChartImage(doc, 'Top Products by Revenue', charts, y, doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight());

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Products', 15, y); y += 7;
    autoTable(doc, {
      startY: y,
      head: [['Product', 'Category', 'Sold', 'Revenue', 'Avg Price', 'Turnover']],
      body: data.products.map((p: any) => [
        p.productName, p.category, this.fmtNum(p.totalSold),
        this.fmtAmt(p.revenue, sym), this.fmtAmt(p.averagePrice, sym), p.stockTurnover.toString(),
      ]),
      theme: 'striped',
      headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
      alternateRowStyles: { fillColor: this.COLORS.lightGray },
      margin: { left: 15, right: 15 }, styles: { fontSize: 8 },
    });
  }

  // ── Revenue ────────────────────────────────────────────────────────────────

  private static generateRevenueReport(doc: jsPDF, report: Report, y: number, sym: string, charts: CapturedChart[] = []): void {
    const data = report.data;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Summary', 15, y); y += 10;
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Total Revenue',       this.fmtAmt(data.summary.totalRevenue, sym)],
        ['Net Revenue',         this.fmtAmt(data.summary.totalNetRevenue, sym)],
        ['Total Orders',        this.fmtNum(data.summary.totalOrders)],
        ['Avg Order Value',     this.fmtAmt(data.summary.averageOrderValue, sym)],
        ['Avg Daily Revenue',   this.fmtAmt(data.summary.averageDailyRevenue, sym)],
        ['Total Tax',           this.fmtAmt(data.summary.totalTax, sym)],
        ['Total Shipping',      this.fmtAmt(data.summary.totalShipping, sym)],
        ['Total Discount',      this.fmtAmt(data.summary.totalDiscount, sym)],
        ['Growth Rate',         `${data.summary.growthRate.toFixed(2)}%`],
        ['Highest Revenue Day', data.summary.highestRevenueDay],
        ['Lowest Revenue Day',  data.summary.lowestRevenueDay],
      ],
      theme: 'plain',
      headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
      margin: { left: 15 }, tableWidth: 100,
    });
    y = (doc as any).lastAutoTable.finalY + 15;

    {
      const pw = doc.internal.pageSize.getWidth(), ph = doc.internal.pageSize.getHeight();
      y = this.addChartImage(doc, 'Daily Revenue Trend', charts, y, pw, ph);
      y = this.addChartImage(doc, 'Revenue by Category', charts, y, pw, ph);
    }

    if (data.topProducts?.length > 0) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('Top Products by Revenue', 15, y); y += 7;
      autoTable(doc, {
        startY: y,
        head: [['Product', 'Units Sold', 'Revenue', '% of Total']],
        body: data.topProducts.slice(0, 10).map((p: any) => [
          p.productName, this.fmtNum(p.totalSold), this.fmtAmt(p.revenue, sym), `${p.percentageOfTotal.toFixed(2)}%`,
        ]),
        theme: 'plain',
        headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
        margin: { left: 15 }, tableWidth: 120, styles: { fontSize: 9 },
      });
      y = (doc as any).lastAutoTable.finalY + 15;
    }

    if (data.topCategories?.length > 0) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('Top Categories by Revenue', 15, y); y += 7;
      autoTable(doc, {
        startY: y,
        head: [['Category', 'Revenue', '% of Total']],
        body: data.topCategories.slice(0, 10).map((c: any) => [
          c.categoryName, this.fmtAmt(c.revenue, sym), `${c.percentageOfTotal.toFixed(2)}%`,
        ]),
        theme: 'plain',
        headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
        margin: { left: 15 }, tableWidth: 100, styles: { fontSize: 9 },
      });
      y = (doc as any).lastAutoTable.finalY + 15;
    }

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Daily Revenue', 15, y); y += 7;
    autoTable(doc, {
      startY: y,
      head: [['Date', 'Orders', 'Revenue', 'Net Revenue', 'Avg Order', 'Tax', 'Shipping']],
      body: data.dailyRevenue.map((d: any) => [
        d.date instanceof Date ? d.date.toLocaleDateString() : 'N/A',
        this.fmtNum(d.totalOrders), this.fmtAmt(d.totalRevenue, sym), this.fmtAmt(d.netRevenue, sym),
        this.fmtAmt(d.averageOrderValue, sym), this.fmtAmt(d.totalTax, sym), this.fmtAmt(d.totalShipping, sym),
      ]),
      theme: 'striped',
      headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
      alternateRowStyles: { fillColor: this.COLORS.lightGray },
      margin: { left: 15, right: 15 }, styles: { fontSize: 7 },
      columnStyles: { 0: { cellWidth: 24 }, 1: { cellWidth: 18 }, 2: { cellWidth: 28 }, 3: { cellWidth: 28 }, 4: { cellWidth: 24 }, 5: { cellWidth: 20 }, 6: { cellWidth: 22 } },
    });
  }

  // ── Returns & Cancellations ────────────────────────────────────────────────

  private static generateReturnsCancellationsReport(doc: jsPDF, report: Report, y: number, sym: string, charts: CapturedChart[] = []): void {
    const data = report.data;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Summary', 15, y); y += 10;
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Total Returned',      this.fmtNum(data.summary.totalReturned)],
        ['Total Cancelled',     this.fmtNum(data.summary.totalCancelled)],
        ['Total Refunded',      this.fmtNum(data.summary.totalRefunded)],
        ['Total Orders',        this.fmtNum(data.summary.totalOrders)],
        ['Total Lost Revenue',  this.fmtAmt(data.summary.totalLostRevenue, sym)],
        ['Return Rate',         `${data.summary.returnRate}%`],
        ['Cancellation Rate',   `${data.summary.cancellationRate}%`],
      ],
      theme: 'plain',
      headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
      margin: { left: 15 }, tableWidth: 90,
    });
    y = (doc as any).lastAutoTable.finalY + 15;

    {
      const pw = doc.internal.pageSize.getWidth(), ph = doc.internal.pageSize.getHeight();
      y = this.addChartImage(doc, 'Returned vs Cancelled vs Refunded', charts, y, pw, ph);
      y = this.addChartImage(doc, 'Top Returned Products (Lost Revenue)', charts, y, pw, ph);
    }

    if (data.summary.topReturnedProducts?.length > 0) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('Top Returned Products', 15, y); y += 7;
      autoTable(doc, {
        startY: y,
        head: [['Product', 'Quantity', 'Lost Revenue']],
        body: data.summary.topReturnedProducts.slice(0, 10).map((p: any) => [
          p.productName, this.fmtNum(p.quantity), this.fmtAmt(p.revenue, sym),
        ]),
        theme: 'plain',
        headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
        margin: { left: 15 }, tableWidth: 110, styles: { fontSize: 9 },
      });
      y = (doc as any).lastAutoTable.finalY + 15;
    }

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Orders', 15, y); y += 7;
    autoTable(doc, {
      startY: y,
      head: [['Order #', 'Customer', 'Date', 'Amount', 'Status', 'Payment', 'Reason']],
      body: data.orders.map((o: any) => [
        o.orderNumber, o.customerName,
        o.orderDate instanceof Date ? o.orderDate.toLocaleDateString() : 'N/A',
        this.fmtAmt(o.totalAmount, sym), o.status, o.paymentMethod, o.reason || '—',
      ]),
      theme: 'striped',
      headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
      alternateRowStyles: { fillColor: this.COLORS.lightGray },
      margin: { left: 15, right: 15 }, styles: { fontSize: 8 },
      columnStyles: { 0: { cellWidth: 26 }, 1: { cellWidth: 32 }, 2: { cellWidth: 22 }, 3: { cellWidth: 28 }, 4: { cellWidth: 22 }, 5: { cellWidth: 24 }, 6: { cellWidth: 28 } },
    });
  }

  // ── Profit & Loss ──────────────────────────────────────────────────────────

  private static generateProfitLossReport(doc: jsPDF, report: Report, y: number, sym: string, charts: CapturedChart[] = []): void {
    const data = report.data;
    const s = data.summary;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Summary', 15, y); y += 10;
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Period',                     `${s.periodDays} day(s)`],
        ['Delivered Orders',           this.fmtNum(s.totalOrders)],
        ['Revenue',                    this.fmtAmt(s.revenue, sym)],
        ['Cost of Goods Sold (COGS)',  this.fmtAmt(s.cogs, sym)],
        ['Gross Profit',               this.fmtAmt(s.grossProfit, sym)],
        ['Gross Margin',               `${s.grossMarginPercent.toFixed(2)}%`],
        ['Operating Costs',            this.fmtAmt(s.operatingCosts, sym)],
        ['  — One-Time',               this.fmtAmt(s.operatingCostsOneTime, sym)],
        ['  — Recurring (prorated)',   this.fmtAmt(s.operatingCostsRecurring, sym)],
        ['Net Profit',                 this.fmtAmt(s.netProfit, sym)],
        ['Net Margin',                 `${s.netMarginPercent.toFixed(2)}%`],
      ],
      theme: 'plain',
      headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
      margin: { left: 15 }, tableWidth: 100,
    });
    y = (doc as any).lastAutoTable.finalY + 15;

    {
      const pw = doc.internal.pageSize.getWidth(), ph = doc.internal.pageSize.getHeight();
      y = this.addChartImage(doc, 'Revenue → Profit Waterfall', charts, y, pw, ph);
      y = this.addChartImage(doc, 'Operating Costs by Category', charts, y, pw, ph);
    }

    if (data.costBreakdown?.length > 0) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('Operating Costs by Category', 15, y); y += 7;
      autoTable(doc, {
        startY: y,
        head: [['Category', 'One-Time', 'Recurring', 'Total', 'Entries']],
        body: data.costBreakdown.map((c: any) => [
          c.category, this.fmtAmt(c.oneTimeAmount, sym), this.fmtAmt(c.recurringAmount, sym),
          this.fmtAmt(c.amount, sym), this.fmtNum(c.count),
        ]),
        theme: 'plain',
        headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
        margin: { left: 15 }, tableWidth: 140, styles: { fontSize: 9 },
      });
      y = (doc as any).lastAutoTable.finalY + 15;
    }

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Revenue & Profit by Product', 15, y); y += 7;
    if (data.revenueByProduct?.length > 0) {
      autoTable(doc, {
        startY: y,
        head: [['Product', 'Qty Sold', 'Revenue', 'COGS', 'Gross Profit']],
        body: data.revenueByProduct.map((p: any) => [
          p.productName, this.fmtNum(p.quantitySold), this.fmtAmt(p.revenue, sym),
          this.fmtAmt(p.cogs, sym), this.fmtAmt(p.grossProfit, sym),
        ]),
        theme: 'striped',
        headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
        alternateRowStyles: { fillColor: this.COLORS.lightGray },
        margin: { left: 15, right: 15 }, styles: { fontSize: 8 },
      });
    } else {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
      doc.text('No delivered orders in this date range.', 15, y);
    }
  }

  private static generateCostReport(doc: jsPDF, report: Report, y: number, sym: string, charts: CapturedChart[] = []): void {
    const data = report.data;
    const s = data.summary;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Summary', 15, y); y += 10;
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Period',                       `${s.periodDays} day(s)`],
        ['Total Cost (Op + Purchases)',  this.fmtAmt(s.totalCost, sym)],
        ['Operating Costs',              this.fmtAmt(s.operatingCosts, sym)],
        ['  — One-Time',                 this.fmtAmt(s.operatingCostsOneTime, sym)],
        ['  — Recurring (prorated)',     this.fmtAmt(s.operatingCostsRecurring, sym)],
        ['Purchases / Imports',          this.fmtAmt(s.purchaseCosts, sym)],
        ['  — Paid',                     this.fmtAmt(s.purchaseCostsPaid, sym)],
        ['  — Outstanding',              this.fmtAmt(s.purchaseCostsOutstanding, sym)],
        ['COGS (goods sold)',            this.fmtAmt(s.cogs, sym)],
        ['Top Cost Category',            s.topCostCategory],
        ['Top Supplier',                 s.topCostSupplier],
      ],
      theme: 'plain',
      headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
      margin: { left: 15 }, tableWidth: 120,
    });
    y = (doc as any).lastAutoTable.finalY + 15;

    {
      const pw = doc.internal.pageSize.getWidth(), ph = doc.internal.pageSize.getHeight();
      y = this.addChartImage(doc, 'Cost Trend by Month', charts, y, pw, ph);
      y = this.addChartImage(doc, 'Cost Composition', charts, y, pw, ph);
      y = this.addChartImage(doc, 'Operating Cost by Category', charts, y, pw, ph);
      y = this.addChartImage(doc, 'Purchase Spend by Supplier', charts, y, pw, ph);
    }

    if (data.costByCategory?.length > 0) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('Operating Costs by Category', 15, y); y += 7;
      autoTable(doc, {
        startY: y,
        head: [['Category', 'One-Time', 'Recurring', 'Total', '% Op.']],
        body: data.costByCategory.map((c: any) => [
          c.category, this.fmtAmt(c.oneTimeAmount, sym), this.fmtAmt(c.recurringAmount, sym),
          this.fmtAmt(c.amount, sym), `${c.percentageOfOperating.toFixed(1)}%`,
        ]),
        theme: 'plain',
        headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
        margin: { left: 15 }, tableWidth: 160, styles: { fontSize: 9 },
      });
      y = (doc as any).lastAutoTable.finalY + 15;
    }

    if (data.costBySupplier?.length > 0) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('Purchases / Imports by Supplier', 15, y); y += 7;
      autoTable(doc, {
        startY: y,
        head: [['Supplier', 'POs', 'Purchased', 'Paid', 'Outstanding']],
        body: data.costBySupplier.map((c: any) => [
          c.supplierName, this.fmtNum(c.poCount), this.fmtAmt(c.totalPurchased, sym),
          this.fmtAmt(c.totalPaid, sym), this.fmtAmt(c.outstanding, sym),
        ]),
        theme: 'striped',
        headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
        alternateRowStyles: { fillColor: this.COLORS.lightGray },
        margin: { left: 15, right: 15 }, styles: { fontSize: 8 },
      });
    }
  }

  private static generatePaymentsReport(doc: jsPDF, report: Report, y: number, sym: string, charts: CapturedChart[] = []): void {
    const data = report.data;
    const s = data.summary;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Summary', 15, y); y += 10;
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Received (Customers)',   this.fmtAmt(s.totalReceived, sym)],
        ['Customer Dues',          this.fmtAmt(s.totalCustomerDue, sym)],
        ['  — Due Orders',         this.fmtNum(s.customerDueCount)],
        ['Paid to Suppliers',      this.fmtAmt(s.totalPaidToSuppliers, sym)],
        ['Supplier Arrears',       this.fmtAmt(s.totalSupplierArrears, sym)],
        ['  — Arrear POs',         this.fmtNum(s.supplierArrearsCount)],
        ['Net Cash Position',      this.fmtAmt(s.netCashPosition, sym)],
      ],
      theme: 'plain',
      headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
      margin: { left: 15 }, tableWidth: 120,
    });
    y = (doc as any).lastAutoTable.finalY + 15;

    {
      const pw = doc.internal.pageSize.getWidth(), ph = doc.internal.pageSize.getHeight();
      y = this.addChartImage(doc, 'Received vs. Paid Out by Month', charts, y, pw, ph);
      y = this.addChartImage(doc, 'Payments Received by Method', charts, y, pw, ph);
    }

    if (data.incomingByMethod?.length > 0) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('Payments Received by Method', 15, y); y += 7;
      autoTable(doc, {
        startY: y,
        head: [['Method', 'Orders', 'Amount', '% of Received']],
        body: data.incomingByMethod.map((m: any) => [
          m.method, this.fmtNum(m.orderCount), this.fmtAmt(m.amount, sym), `${m.percentageOfReceived.toFixed(1)}%`,
        ]),
        theme: 'plain',
        headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
        margin: { left: 15 }, tableWidth: 140, styles: { fontSize: 9 },
      });
      y = (doc as any).lastAutoTable.finalY + 15;
    }

    if (data.supplierArrears?.length > 0) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('Supplier Arrears (Outstanding Payables)', 15, y); y += 7;
      autoTable(doc, {
        startY: y,
        head: [['PO #', 'Supplier', 'Total', 'Paid', 'Outstanding', 'Status']],
        body: data.supplierArrears.map((a: any) => [
          a.poNumber, a.supplierName, this.fmtAmt(a.totalAmount, sym),
          this.fmtAmt(a.paidAmount, sym), this.fmtAmt(a.outstanding, sym), a.status,
        ]),
        theme: 'striped',
        headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
        alternateRowStyles: { fillColor: this.COLORS.lightGray },
        margin: { left: 15, right: 15 }, styles: { fontSize: 8 },
      });
      y = (doc as any).lastAutoTable.finalY + 15;
    }

    if (data.customerDues?.length > 0) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('Customer Dues (Unpaid / Partial Orders)', 15, y); y += 7;
      autoTable(doc, {
        startY: y,
        head: [['Order #', 'Customer', 'Total', 'Paid', 'Due', 'Status']],
        body: data.customerDues.map((d: any) => [
          d.orderNumber, d.customerName, this.fmtAmt(d.totalAmount, sym),
          this.fmtAmt(d.paidAmount, sym), this.fmtAmt(d.due, sym), d.paymentStatus,
        ]),
        theme: 'striped',
        headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
        alternateRowStyles: { fillColor: this.COLORS.lightGray },
        margin: { left: 15, right: 15 }, styles: { fontSize: 8 },
      });
    }
  }

  // ── AI Forecast ────────────────────────────────────────────────────────────
  private static generateForecastReport(doc: jsPDF, report: Report, y: number, sym: string, charts: CapturedChart[] = []): void {
    const data = report.data;
    if (!data?.predictions) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(12);
      doc.text('No forecast data available.', 15, y);
      return;
    }
    const s = data.summary;
    const isCurrency = data.forecastType === 'revenue';
    const fmtVal = (n: number) => (isCurrency ? this.fmtAmt(n, sym) : this.fmtNum(Math.round(n)));

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Summary', 15, y); y += 10;
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Forecasting',         data.forecastType],
        ['Forecast Period',     `${data.forecastPeriod} days`],
        ['Confidence',          `${(data.confidence?.level || 'medium').toUpperCase()} (${data.confidence?.score ?? '—'})`],
        ['Expected Trend',      `${(s?.expectedGrowth ?? 0) >= 0 ? '+' : ''}${(s?.expectedGrowth ?? 0).toFixed(2)}%`],
        ['Avg Predicted / Day', fmtVal(s?.averagePredictedValue || 0)],
        ['Total Predicted',     fmtVal(s?.totalPredicted || 0)],
        ['Peak Day',            `${s?.peakDate} — ${fmtVal(s?.peakValue || 0)}`],
        ['Lowest Day',          `${s?.lowDate} — ${fmtVal(s?.lowValue || 0)}`],
      ],
      theme: 'plain',
      headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
      margin: { left: 15 }, tableWidth: 110,
    });
    y = (doc as any).lastAutoTable.finalY + 15;

    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    y = this.addChartImage(doc, 'Historical vs. Predicted', charts, y, pw, ph);

    if (y + 20 > ph - 15) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('Day-by-Day Predictions', 15, y); y += 7;
    autoTable(doc, {
      startY: y,
      head: [['Date', 'Predicted', 'Lower Bound', 'Upper Bound', 'Confidence']],
      body: data.predictions.map((p: any) => [
        p.date instanceof Date ? p.date.toLocaleDateString() : 'N/A',
        fmtVal(p.predictedValue), fmtVal(p.lowerBound), fmtVal(p.upperBound),
        `${Math.round((p.confidence || 0) * (p.confidence <= 1 ? 100 : 1))}%`,
      ]),
      theme: 'striped',
      headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
      alternateRowStyles: { fillColor: this.COLORS.lightGray },
      margin: { left: 15, right: 15 }, styles: { fontSize: 8 },
    });
    y = (doc as any).lastAutoTable.finalY + 15;

    const ai = report.aiInsights;
    if (ai) {
      if (y + 20 > ph - 15) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text('AI Insights', 15, y); y += 8;

      if (ai.summary) {
        doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
        const lines = doc.splitTextToSize(ai.summary, pw - 30);
        doc.text(lines, 15, y); y += lines.length * 4.5 + 6;
      }

      const section = (title: string, items?: string[]) => {
        if (!items || items.length === 0) return;
        if (y + 10 > ph - 15) { doc.addPage(); y = 20; }
        doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.text(title, 15, y); y += 5;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
        items.forEach((item) => {
          const lines = doc.splitTextToSize(`• ${item}`, pw - 34);
          if (y + lines.length * 4.5 > ph - 15) { doc.addPage(); y = 20; }
          doc.text(lines, 18, y); y += lines.length * 4.5 + 1;
        });
        y += 4;
      };

      section('Key Findings', ai.keyFindings);
      section('Recommendations', ai.recommendations);
      section('Trends', ai.trends);
      section('Risk Factors', ai.riskFactors);
      section('Opportunities', ai.opportunities);
    }
  }
}

// import jsPDF from 'jspdf';
// import autoTable from 'jspdf-autotable';
// import { Report } from './Report';
// import { getCurrencySymbol } from '../../../util/productUtils';

// export class ReportPDFGenerator {
//   private static readonly COLORS = {
//     primary: '#1976d2',
//     secondary: '#424242',
//     text: '#212121',
//     lightGray: '#f5f5f5',
//     border: '#e0e0e0',
//   };

//   static async generatePDF(report: Report, currency: string = 'LKR'): Promise<void> {
//     const currencySymbol = getCurrencySymbol(currency);
//     const doc = new jsPDF();
//     const pageWidth = doc.internal.pageSize.getWidth();
//     const pageHeight = doc.internal.pageSize.getHeight();
//     let yPosition = 20;

//     // Header
//     doc.setFillColor(this.COLORS.primary);
//     doc.rect(0, 0, pageWidth, 40, 'F');

//     doc.setTextColor(255, 255, 255);
//     doc.setFontSize(20);
//     doc.setFont('helvetica', 'bold');
//     doc.text(report.reportName, 15, 20);

//     doc.setFontSize(10);
//     doc.setFont('helvetica', 'normal');
//     doc.text(`Generated: ${report.generatedAt?.toLocaleString()}`, 15, 30);
//     doc.text(`By: ${report.generatedByName}`, 15, 35);

//     yPosition = 50;

//     // Report Info
//     doc.setTextColor(this.COLORS.text);
//     doc.setFontSize(10);

//     if (report.description) {
//       doc.setFont('helvetica', 'bold');
//       doc.text('Description:', 15, yPosition);
//       doc.setFont('helvetica', 'normal');
//       const descLines = doc.splitTextToSize(report.description, pageWidth - 30);
//       doc.text(descLines, 15, yPosition + 5);
//       yPosition += descLines.length * 5 + 10;
//     }

//     // Filters
//     if (report.filters && Object.keys(report.filters).length > 0) {
//       doc.setFont('helvetica', 'bold');
//       doc.text('Filters Applied:', 15, yPosition);
//       yPosition += 7;

//       doc.setFont('helvetica', 'normal');
//       doc.setFontSize(9);

//       if (report.filters.dateFrom) {
//         doc.text(`Date From: ${report.filters.dateFrom.toLocaleDateString()}`, 20, yPosition);
//         yPosition += 5;
//       }
//       if (report.filters.dateTo) {
//         doc.text(`Date To: ${report.filters.dateTo.toLocaleDateString()}`, 20, yPosition);
//         yPosition += 5;
//       }

//       yPosition += 5;
//       doc.setFontSize(10);
//     }

//     // Generate report content based on type
//     switch (report.reportType) {
//       case 'sales':
//         this.generateSalesReport(doc, report, yPosition, currencySymbol);
//         break;
//       case 'inventory':
//         this.generateInventoryReport(doc, report, yPosition, currencySymbol);
//         break;
//       case 'purchase_orders':
//         this.generatePurchaseOrderReport(doc, report, yPosition, currencySymbol);
//         break;
//       case 'low_stock':
//         this.generateLowStockReport(doc, report, yPosition, currencySymbol);
//         break;
//       case 'warehouse_performance':
//         this.generateWarehousePerformanceReport(doc, report, yPosition, currencySymbol);
//         break;
//       case 'product_performance':
//         this.generateProductPerformanceReport(doc, report, yPosition, currencySymbol);
//         break;
//       case 'revenue':
//         this.generateRevenueReport(doc, report, yPosition, currencySymbol);
//         break;
//     }

//     // Footer on each page
//     const totalPages = doc.internal.pages.length - 1;
//     for (let i = 1; i <= totalPages; i++) {
//       doc.setPage(i);
//       doc.setFontSize(8);
//       doc.setTextColor(128, 128, 128);
//       doc.text(
//         `Page ${i} of ${totalPages}`,
//         pageWidth / 2,
//         pageHeight - 10,
//         { align: 'center' }
//       );
//     }

//     // Save PDF
//     const fileName = `${report.reportName.replace(/\s+/g, '_')}_${new Date().getTime()}.pdf`;
//     doc.save(fileName);
//   }

//   private static generateSalesReport(doc: jsPDF, report: Report, yPosition: number, currencySymbol: string): void {
//     const data = report.data;

//     // Summary Section
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(14);
//     doc.text('Summary', 15, yPosition);
//     yPosition += 10;

//     const summaryData = [
//       ['Total Orders', data.summary.totalOrders.toString()],
//       ['Total Revenue', `${currencySymbol} ${data.summary.totalRevenue.toFixed(2)}`],
//       ['Average Order Value', `${currencySymbol} ${data.summary.averageOrderValue.toFixed(2)}`],
//       ['Total Tax', `${currencySymbol} ${data.summary.totalTax.toFixed(2)}`],
//       ['Total Shipping', `${currencySymbol} ${data.summary.totalShipping.toFixed(2)}`],
//       ['Total Discount', `${currencySymbol} ${data.summary.totalDiscount.toFixed(2)}`],
//     ];

//     autoTable(doc, {
//       startY: yPosition,
//       head: [['Metric', 'Value']],
//       body: summaryData,
//       theme: 'plain',
//       headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
//       margin: { left: 15 },
//       tableWidth: 80,
//     });

//     yPosition = (doc as any).lastAutoTable.finalY + 15;

//     // Orders Table
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(14);
//     doc.text('Orders', 15, yPosition);
//     yPosition += 7;

//     const orderRows = data.orders.map((order: any) => [
//       order.orderNumber,
//       order.customerName,
//       order.orderDate instanceof Date 
//         ? order.orderDate.toLocaleDateString() 
//         : 'N/A',
//       `${currencySymbol} ${order.totalAmount.toFixed(2)}`,
//       order.paymentMethod,
//       order.status,
//     ]);

//     autoTable(doc, {
//       startY: yPosition,
//       head: [['Order #', 'Customer', 'Date', 'Amount', 'Payment', 'Status']],
//       body: orderRows,
//       theme: 'striped',
//       headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
//       alternateRowStyles: { fillColor: this.COLORS.lightGray },
//       margin: { left: 15, right: 15 },
//       styles: { fontSize: 8 },
//     });
//   }

//   private static generateInventoryReport(doc: jsPDF, report: Report, yPosition: number, currencySymbol: string): void {
//     const data = report.data;

//     // Summary Section
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(14);
//     doc.text('Summary', 15, yPosition);
//     yPosition += 10;

//     const summaryData = [
//       ['Total Products', data.summary.totalProducts.toString()],
//       ['Total Variants', data.summary.totalVariants.toString()],
//       ['Total Stock', data.summary.totalStock.toString()],
//       ['Stock Value', `${currencySymbol} ${data.summary.totalValue.toFixed(2)}`],
//       ['In Stock Items', data.summary.inStockItems.toString()],
//       ['Low Stock Items', data.summary.lowStockItems.toString()],
//       ['Out of Stock Items', data.summary.outOfStockItems.toString()],
//     ];

//     autoTable(doc, {
//       startY: yPosition,
//       head: [['Metric', 'Value']],
//       body: summaryData,
//       theme: 'plain',
//       headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
//       margin: { left: 15 },
//       tableWidth: 80,
//     });

//     yPosition = (doc as any).lastAutoTable.finalY + 15;

//     // Products Table
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(14);
//     doc.text('Products', 15, yPosition);
//     yPosition += 7;

//     const productRows = data.products.map((product: any) => [
//       product.productName + (product.hasVariants ? ` (${product.variants?.length || 0} variants)` : ''),
//       product.sku,
//       product.category,
//       product.totalStock.toString(),
//       `${currencySymbol} ${product.stockValue.toFixed(2)}`,
//       product.status.replace('_', ' '),
//     ]);

//     autoTable(doc, {
//       startY: yPosition,
//       head: [['Product', 'SKU', 'Category', 'Stock', 'Value', 'Status']],
//       body: productRows,
//       theme: 'striped',
//       headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
//       alternateRowStyles: { fillColor: this.COLORS.lightGray },
//       margin: { left: 15, right: 15 },
//       styles: { fontSize: 8 },
//     });
//   }

//   private static generatePurchaseOrderReport(doc: jsPDF, report: Report, yPosition: number, currencySymbol: string): void {
//     const data = report.data;

//     if (!data.purchaseOrders || data.purchaseOrders.length === 0) {
//       doc.setFont('helvetica', 'normal');
//       doc.setFontSize(12);
//       doc.text('No purchase orders found for the selected criteria.', 15, yPosition);
//       return;
//     }

//     // Summary Section
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(14);
//     doc.text('Summary', 15, yPosition);
//     yPosition += 10;

//     const summaryData = [
//       ['Total Purchase Orders', data.summary.totalPOs.toString()],
//       ['Total Amount', `${currencySymbol} ${data.summary.totalAmount?.toFixed(2) || '0.00'}`],
//       ['Total Paid', `${currencySymbol} ${data.summary.totalPaid?.toFixed(2) || '0.00'}`],
//       ['Total Pending', `${currencySymbol} ${((data.summary.totalAmount || 0) - (data.summary.totalPaid || 0)).toFixed(2)}`],
//     ];

//     autoTable(doc, {
//       startY: yPosition,
//       head: [['Metric', 'Value']],
//       body: summaryData,
//       theme: 'plain',
//       headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
//       margin: { left: 15 },
//       tableWidth: 80,
//     });

//     yPosition = (doc as any).lastAutoTable.finalY + 15;

//     // Status Breakdown
//     if (data.summary.statusBreakdown && Object.keys(data.summary.statusBreakdown).length > 0) {
//       doc.setFont('helvetica', 'bold');
//       doc.setFontSize(12);
//       doc.text('Status Breakdown', 15, yPosition);
//       yPosition += 7;

//       const statusBreakdown = data.summary.statusBreakdown as Record<string, number>;
//       const statusRows = Object.entries(statusBreakdown).map(([status, count]) => [
//         status,
//         count.toString(),
//       ]);

//       autoTable(doc, {
//         startY: yPosition,
//         head: [['Status', 'Count']],
//         body: statusRows,
//         theme: 'plain',
//         headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
//         margin: { left: 15 },
//         tableWidth: 60,
//       });

//       yPosition = (doc as any).lastAutoTable.finalY + 10;
//     }

//     // Payment Status Breakdown
//     if (data.summary.paymentStatusBreakdown && Object.keys(data.summary.paymentStatusBreakdown).length > 0) {
//       doc.setFont('helvetica', 'bold');
//       doc.setFontSize(12);
//       doc.text('Payment Status Breakdown', 15, yPosition);
//       yPosition += 7;

//       const paymentStatusBreakdown = data.summary.paymentStatusBreakdown as Record<string, number>;
//       const paymentStatusRows = Object.entries(paymentStatusBreakdown).map(([status, count]) => [
//         status,
//         count.toString(),
//       ]);

//       autoTable(doc, {
//         startY: yPosition,
//         head: [['Payment Status', 'Count']],
//         body: paymentStatusRows,
//         theme: 'plain',
//         headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
//         margin: { left: 15 },
//         tableWidth: 60,
//       });

//       yPosition = (doc as any).lastAutoTable.finalY + 15;
//     }

//     // Purchase Orders Table
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(14);
//     doc.text('Purchase Orders', 15, yPosition);
//     yPosition += 7;

//     const poRows = data.purchaseOrders.map((po: any) => [
//       po.poNumber || 'N/A',
//       po.supplierName || 'Unknown',
//       po.orderDate instanceof Date 
//         ? po.orderDate.toLocaleDateString() 
//         : 'N/A',
//       po.expectedDelivery instanceof Date 
//         ? po.expectedDelivery.toLocaleDateString() 
//         : 'N/A',
//       `${currencySymbol} ${po.totalAmount?.toFixed(2) || '0.00'}`,
//       po.status || 'draft',
//       po.paymentStatus || 'pending',
//     ]);

//     autoTable(doc, {
//       startY: yPosition,
//       head: [['PO #', 'Supplier', 'Order Date', 'Expected Delivery', 'Amount', 'Status', 'Payment']],
//       body: poRows,
//       theme: 'striped',
//       headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
//       alternateRowStyles: { fillColor: this.COLORS.lightGray },
//       margin: { left: 15, right: 15 },
//       styles: { fontSize: 8 },
//       columnStyles: {
//         0: { cellWidth: 30 },
//         1: { cellWidth: 40 },
//         2: { cellWidth: 25 },
//         3: { cellWidth: 25 },
//         4: { cellWidth: 25 },
//         5: { cellWidth: 25 },
//         6: { cellWidth: 25 },
//       },
//     });
//   }

//   private static generateLowStockReport(doc: jsPDF, report: Report, yPosition: number, currencySymbol: string): void {
//     const data = report.data;

//     // Summary Section
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(14);
//     doc.text('Summary', 15, yPosition);
//     yPosition += 10;

//     const summaryData = [
//       ['Low Stock Items', data.summary.totalLowStockItems.toString()],
//       ['Out of Stock Items', data.summary.totalOutOfStockItems.toString()],
//       ['Affected Products', data.summary.affectedProducts.toString()],
//       ['Affected Warehouses', data.summary.affectedWarehouses.toString()],
//     ];

//     autoTable(doc, {
//       startY: yPosition,
//       head: [['Metric', 'Value']],
//       body: summaryData,
//       theme: 'plain',
//       headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
//       margin: { left: 15 },
//       tableWidth: 80,
//     });

//     yPosition = (doc as any).lastAutoTable.finalY + 15;

//     // Low Stock Items Table
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(14);
//     doc.text('Low Stock Items', 15, yPosition);
//     yPosition += 7;

//     const itemRows = data.items.map((item: any) => [
//       item.productName + (item.variantName ? ` (${item.variantName})` : ''),
//       item.warehouseName,
//       item.currentStock.toString(),
//       item.minStock.toString(),
//       item.deficit.toString(),
//       item.status === 'out_of_stock' ? 'Out of Stock' : 'Low Stock',
//     ]);

//     autoTable(doc, {
//       startY: yPosition,
//       head: [['Product', 'Warehouse', 'Current', 'Min', 'Deficit', 'Status']],
//       body: itemRows,
//       theme: 'striped',
//       headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
//       alternateRowStyles: { fillColor: this.COLORS.lightGray },
//       margin: { left: 15, right: 15 },
//       styles: { fontSize: 8 },
//     });
//   }

//   private static generateWarehousePerformanceReport(doc: jsPDF, report: Report, yPosition: number, currencySymbol: string): void {
//     const data = report.data;

//     // Summary Section
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(14);
//     doc.text('Summary', 15, yPosition);
//     yPosition += 10;

//     const summaryData = [
//       ['Total Warehouses', data.summary.totalWarehouses.toString()],
//       ['Total Capacity', data.summary.totalCapacity.toString()],
//       ['Total Used', data.summary.totalUsed.toString()],
//       ['Total Available', data.summary.totalAvailable.toString()],
//       ['Average Utilization', `${data.summary.averageUtilization}%`],
//       ['Most Utilized', data.summary.mostUtilizedWarehouse],
//       ['Least Utilized', data.summary.leastUtilizedWarehouse],
//     ];

//     autoTable(doc, {
//       startY: yPosition,
//       head: [['Metric', 'Value']],
//       body: summaryData,
//       theme: 'plain',
//       headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
//       margin: { left: 15 },
//       tableWidth: 100,
//     });

//     yPosition = (doc as any).lastAutoTable.finalY + 15;

//     // Warehouses Table
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(14);
//     doc.text('Warehouses', 15, yPosition);
//     yPosition += 7;

//     const warehouseRows = data.warehouses.map((warehouse: any) => [
//       warehouse.warehouseName,
//       warehouse.code,
//       warehouse.totalProducts.toString(),
//       warehouse.totalStock.toString(),
//       `${warehouse.capacityUsed}/${warehouse.capacityTotal}`,
//       `${warehouse.utilizationRate}%`,
//     ]);

//     autoTable(doc, {
//       startY: yPosition,
//       head: [['Warehouse', 'Code', 'Products', 'Stock', 'Capacity', 'Utilization']],
//       body: warehouseRows,
//       theme: 'striped',
//       headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
//       alternateRowStyles: { fillColor: this.COLORS.lightGray },
//       margin: { left: 15, right: 15 },
//       styles: { fontSize: 8 },
//     });
//   }

//   private static generateProductPerformanceReport(doc: jsPDF, report: Report, yPosition: number, currencySymbol: string): void {
//     const data = report.data;

//     // Summary Section
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(14);
//     doc.text('Summary', 15, yPosition);
//     yPosition += 10;

//     const summaryData = [
//       ['Total Products', data.summary.totalProducts.toString()],
//       ['Total Revenue', `${currencySymbol} ${data.summary.totalRevenue.toFixed(2)}`],
//       ['Total Units Sold', data.summary.totalUnitsSold.toString()],
//       ['Best Selling Product', data.summary.bestSellingProduct],
//       ['Average Stock Turnover', data.summary.averageStockTurnover.toString()],
//       ['Average Discount', `${data.summary.averageDiscount.toFixed(2)}%`],
//     ];

//     autoTable(doc, {
//       startY: yPosition,
//       head: [['Metric', 'Value']],
//       body: summaryData,
//       theme: 'plain',
//       headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
//       margin: { left: 15 },
//       tableWidth: 100,
//     });

//     yPosition = (doc as any).lastAutoTable.finalY + 15;

//     // Products Table
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(14);
//     doc.text('Products', 15, yPosition);
//     yPosition += 7;

//     const productRows = data.products.map((product: any) => [
//       product.productName,
//       product.category,
//       product.totalSold.toString(),
//       `${currencySymbol} ${product.revenue.toFixed(2)}`,
//       `${currencySymbol} ${product.averagePrice.toFixed(2)}`,
//       product.stockTurnover.toString(),
//     ]);

//     autoTable(doc, {
//       startY: yPosition,
//       head: [['Product', 'Category', 'Sold', 'Revenue', 'Avg Price', 'Turnover']],
//       body: productRows,
//       theme: 'striped',
//       headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
//       alternateRowStyles: { fillColor: this.COLORS.lightGray },
//       margin: { left: 15, right: 15 },
//       styles: { fontSize: 8 },
//     });
//   }

//   private static generateRevenueReport(doc: jsPDF, report: Report, yPosition: number, currencySymbol: string): void {
//     const data = report.data;

//     // Summary Section
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(14);
//     doc.text('Summary', 15, yPosition);
//     yPosition += 10;

//     const summaryData = [
//       ['Total Revenue', `${currencySymbol} ${data.summary.totalRevenue.toFixed(2)}`],
//       ['Net Revenue', `${currencySymbol} ${data.summary.totalNetRevenue.toFixed(2)}`],
//       ['Total Orders', data.summary.totalOrders.toString()],
//       ['Average Order Value', `${currencySymbol} ${data.summary.averageOrderValue.toFixed(2)}`],
//       ['Average Daily Revenue', `${currencySymbol} ${data.summary.averageDailyRevenue.toFixed(2)}`],
//       ['Total Tax', `${currencySymbol} ${data.summary.totalTax.toFixed(2)}`],
//       ['Total Shipping', `${currencySymbol} ${data.summary.totalShipping.toFixed(2)}`],
//       ['Total Discount', `${currencySymbol} ${data.summary.totalDiscount.toFixed(2)}`],
//       ['Growth Rate', `${data.summary.growthRate.toFixed(2)}%`],
//       ['Highest Revenue Day', data.summary.highestRevenueDay],
//       ['Lowest Revenue Day', data.summary.lowestRevenueDay],
//     ];

//     autoTable(doc, {
//       startY: yPosition,
//       head: [['Metric', 'Value']],
//       body: summaryData,
//       theme: 'plain',
//       headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
//       margin: { left: 15 },
//       tableWidth: 100,
//     });

//     yPosition = (doc as any).lastAutoTable.finalY + 15;

//     // Top Products
//     if (data.topProducts && data.topProducts.length > 0) {
//       doc.setFont('helvetica', 'bold');
//       doc.setFontSize(12);
//       doc.text('Top Products by Revenue', 15, yPosition);
//       yPosition += 7;

//       const topProductRows = data.topProducts.slice(0, 10).map((product: any) => [
//         product.productName,
//         product.totalSold.toString(),
//         `${currencySymbol} ${product.revenue.toFixed(2)}`,
//         `${product.percentageOfTotal.toFixed(2)}%`,
//       ]);

//       autoTable(doc, {
//         startY: yPosition,
//         head: [['Product', 'Units Sold', 'Revenue', '% of Total']],
//         body: topProductRows,
//         theme: 'plain',
//         headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
//         margin: { left: 15 },
//         tableWidth: 120,
//         styles: { fontSize: 9 },
//       });

//       yPosition = (doc as any).lastAutoTable.finalY + 15;
//     }

//     // Top Categories
//     if (data.topCategories && data.topCategories.length > 0) {
//       doc.setFont('helvetica', 'bold');
//       doc.setFontSize(12);
//       doc.text('Top Categories by Revenue', 15, yPosition);
//       yPosition += 7;

//       const topCategoryRows = data.topCategories.slice(0, 10).map((category: any) => [
//         category.categoryName,
//         `${currencySymbol} ${category.revenue.toFixed(2)}`,
//         `${category.percentageOfTotal.toFixed(2)}%`,
//       ]);

//       autoTable(doc, {
//         startY: yPosition,
//         head: [['Category', 'Revenue', '% of Total']],
//         body: topCategoryRows,
//         theme: 'plain',
//         headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
//         margin: { left: 15 },
//         tableWidth: 100,
//         styles: { fontSize: 9 },
//       });

//       yPosition = (doc as any).lastAutoTable.finalY + 15;
//     }

//     // Daily Revenue Table
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(14);
//     doc.text('Daily Revenue', 15, yPosition);
//     yPosition += 7;

//     const dailyRows = data.dailyRevenue.map((day: any) => [
//       day.date instanceof Date 
//         ? day.date.toLocaleDateString() 
//         : 'N/A',
//       day.totalOrders.toString(),
//       `${currencySymbol} ${day.totalRevenue.toFixed(2)}`,
//       `${currencySymbol} ${day.netRevenue.toFixed(2)}`,
//       `${currencySymbol} ${day.averageOrderValue.toFixed(2)}`,
//       `${currencySymbol} ${day.totalTax.toFixed(2)}`,
//       `${currencySymbol} ${day.totalShipping.toFixed(2)}`,
//     ]);

//     autoTable(doc, {
//       startY: yPosition,
//       head: [['Date', 'Orders', 'Revenue', 'Net Revenue', 'Avg Order', 'Tax', 'Shipping']],
//       body: dailyRows,
//       theme: 'striped',
//       headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
//       alternateRowStyles: { fillColor: this.COLORS.lightGray },
//       margin: { left: 15, right: 15 },
//       styles: { fontSize: 7 },
//       columnStyles: {
//         0: { cellWidth: 25 },
//         1: { cellWidth: 20 },
//         2: { cellWidth: 30 },
//         3: { cellWidth: 30 },
//         4: { cellWidth: 25 },
//         5: { cellWidth: 20 },
//         6: { cellWidth: 25 },
//       },
//     });
//   }
// }